import React from 'react'

import { ZariContext } from '_zari/context'
import { Modal, Button, Form, Col, Row } from '_zari/core'

import * as UbicacionService from 'services/ubicacionAlmacenamiento.service'
import * as ProductoService from 'services/producto.service'

const ModalCelda = ({ show, handleClose, celda: celdaProp, onActualizado }) => {
  const __zariContext = React.useContext(ZariContext)

  const [celda, setCelda] = React.useState(celdaProp)
  const [bloqueado, setBloqueado] = React.useState(!!celdaProp.bloqueado)
  const [descripcion, setDescripcion] = React.useState(celdaProp.descripcion || '')
  const [notas, setNotas] = React.useState(celdaProp.notas || '')

  // Asignar producto
  const [busquedaProd, setBusquedaProd] = React.useState('')
  const [resultadosProd, setResultadosProd] = React.useState([])
  const [prodSeleccionado, setProdSeleccionado] = React.useState(null)
  const [cantidadAsignar, setCantidadAsignar] = React.useState('')
  const [notasAsignar, setNotasAsignar] = React.useState('')
  const [buscando, setBuscando] = React.useState(false)
  const timerRef = React.useRef(null)

  const recargar = React.useCallback(() => {
    UbicacionService.obtenerxId(celda._id).then(({ data }) => {
      setCelda(data)
      setBloqueado(!!data.bloqueado)
      setDescripcion(data.descripcion || '')
      setNotas(data.notas || '')
    })
  }, [celda._id])

  // Buscar productos por término (debounced)
  React.useEffect(() => {
    if (!busquedaProd || busquedaProd.length < 2) {
      setResultadosProd([])
      return
    }
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      setBuscando(true)
      ProductoService.obtenerAsInventariosSelect2({ busqueda: busquedaProd, tipo: '' })
        .then(({ data }) => {
          const lista = Array.isArray(data) ? data : data?.results || data?.items || []
          setResultadosProd(lista)
        })
        .catch(() => setResultadosProd([]))
        .finally(() => setBuscando(false))
    }, 300)
    return () => clearTimeout(timerRef.current)
  }, [busquedaProd])

  const guardarConfig = async () => {
    try {
      await UbicacionService.actualizarUbicacion(celda._id, { descripcion, notas, bloqueado })
      __zariContext.snack('success', 'Ubicación actualizada')
      onActualizado()
      recargar()
    } catch (err) {
      __zariContext.snack('error', err?.data?.error || 'Error al actualizar')
    }
  }

  const asignar = async () => {
    if (!prodSeleccionado) {
      __zariContext.snack('error', 'Seleccione un producto')
      return
    }
    const cant = parseFloat(cantidadAsignar) || 0
    if (cant <= 0) {
      __zariContext.snack('error', 'La cantidad debe ser mayor a 0')
      return
    }
    try {
      await UbicacionService.asignarItem(celda._id, {
        _idProducto: prodSeleccionado._id,
        _idProductoPresentacion: prodSeleccionado.value,
        cantidad: cant,
        notas: notasAsignar
      })
      __zariContext.snack('success', 'Producto asignado')
      setProdSeleccionado(null)
      setBusquedaProd('')
      setCantidadAsignar('')
      setNotasAsignar('')
      recargar()
      onActualizado()
    } catch (err) {
      __zariContext.snack('error', err?.data?.error || 'Error al asignar')
    }
  }

  const cambiarCantidad = async (item, nuevaCant) => {
    try {
      await UbicacionService.actualizarItem(celda._id, item._id, { cantidad: nuevaCant })
      recargar()
      onActualizado()
    } catch (err) {
      __zariContext.snack('error', err?.data?.error || 'Error al actualizar')
    }
  }

  const eliminarItem = async (item) => {
    if (!window.confirm(`¿Quitar "${item.nombreProducto}" de esta ubicación?`)) return
    try {
      await UbicacionService.eliminarItem(celda._id, item._id)
      __zariContext.snack('success', 'Item retirado')
      recargar()
      onActualizado()
    } catch (err) {
      __zariContext.snack('error', err?.data?.error || 'Error al eliminar')
    }
  }

  return (
    <Modal show={show} size='lg'>
      <Modal.Header>
        <Modal.Title>
          <i className='fa fa-cube mr-2' />
          Ubicación: <strong>{celda.codigo}</strong>
          {bloqueado && <span className='badge badge-danger ml-2'>BLOQUEADA</span>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='mb-3'>
          <Col md={6}>
            <Form.Group>
              <Form.Label text='DESCRIPCIÓN' md={12} />
              <Col md={12}>
                <Form.InputText
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  placeholder='Ej: Estante de papelería rápida'
                  id='input-desc-celda'
                />
              </Col>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label text='NOTAS DEL ALMACENERO' md={12} />
              <Col md={12}>
                <Form.InputText
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  placeholder='Notas internas'
                  id='input-notas-celda'
                />
              </Col>
            </Form.Group>
          </Col>
        </Row>
        <Row className='mb-3'>
          <Col md={6}>
            <Form.Checkbox
              checked={bloqueado}
              onChange={(e) => setBloqueado(e.target.checked)}
              id='check-bloq-celda'
              label='Bloquear esta ubicación (no recibe nuevos items)'
            />
          </Col>
          <Col md={6} className='text-right'>
            <Button outline color='primary' size='sm' onClick={guardarConfig}>
              <i className='fa fa-save mr-1' />
              Guardar configuración
            </Button>
          </Col>
        </Row>

        <hr />

        <h6 style={{ color: '#1F3A8A' }}>
          <i className='fa fa-boxes mr-1' />
          Productos en esta ubicación ({celda.items?.length || 0})
        </h6>

        {(celda.items || []).length === 0 ? (
          <div className='text-center p-3 text-muted' style={{ background: '#f8f9fa', borderRadius: 4 }}>
            <i className='fa fa-inbox fa-2x mb-2' />
            <div>Sin productos asignados</div>
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-sm table-hover mb-0'>
              <thead style={{ background: '#EEF2FF' }}>
                <tr>
                  <th>CÓDIGO</th>
                  <th>PRODUCTO</th>
                  <th style={{ width: 120 }}>CANTIDAD</th>
                  <th>NOTAS</th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {celda.items.map((it) => (
                  <tr key={it._id}>
                    <td><code>{it.codigoProducto}</code></td>
                    <td>{it.nombreProducto}</td>
                    <td>
                      <Form.InputNumber
                        size='sm'
                        defaultValue={it.cantidad}
                        min={0}
                        id={`cant-${it._id}`}
                        onBlur={(e) => {
                          const nueva = parseFloat(e.target.value) || 0
                          if (nueva !== it.cantidad) cambiarCantidad(it, nueva)
                        }}
                      />
                    </td>
                    <td>{it.notas || '—'}</td>
                    <td>
                      <Button size='sm' outline color='danger' onClick={() => eliminarItem(it)}>
                        <i className='fa fa-trash' />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!bloqueado && (
          <div className='ub-asignar-box'>
            <h6>
              <i className='fa fa-plus-circle mr-1' style={{ color: '#28a745' }} />
              Asignar producto
            </h6>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label text='BUSCAR PRODUCTO' md={12} />
                  <Col md={12}>
                    <Form.InputText
                      value={busquedaProd}
                      onChange={(e) => {
                        setBusquedaProd(e.target.value)
                        setProdSeleccionado(null)
                      }}
                      placeholder='Mín. 2 caracteres (código o nombre)'
                      id='input-busc-prod'
                    />
                    {buscando && <small className='text-muted'>Buscando...</small>}
                    {!prodSeleccionado && resultadosProd.length > 0 && (
                      <div className='ub-search-results'>
                        {resultadosProd.slice(0, 8).map((p) => (
                          <button
                            key={p._id || p.value}
                            type='button'
                            className='ub-search-item'
                            onClick={() => {
                              setProdSeleccionado(p)
                              setBusquedaProd(p.label || `${p.codigo || ''} ${p.nombre || ''}`)
                              setResultadosProd([])
                            }}
                          >
                            {p.label || `${p.codigo || ''} ${p.nombre || ''}`}
                          </button>
                        ))}
                      </div>
                    )}
                  </Col>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label text='CANTIDAD' md={12} />
                  <Col md={12}>
                    <Form.InputNumber
                      value={cantidadAsignar}
                      onChange={(e) => setCantidadAsignar(e.target.value)}
                      min={0}
                      id='input-cant-asig'
                    />
                  </Col>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label text='NOTAS' md={12} />
                  <Col md={12}>
                    <Form.InputText
                      value={notasAsignar}
                      onChange={(e) => setNotasAsignar(e.target.value)}
                      id='input-notas-asig'
                      placeholder='(opcional)'
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <div className='text-right'>
              <Button color='success' onClick={asignar} disabled={!prodSeleccionado || !cantidadAsignar}>
                <i className='fa fa-check mr-1' /> Asignar a {celda.codigo}
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button outline color='secondary' onClick={handleClose}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ModalCelda
